import React from "react";
import { toast } from "sonner";
import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";
import Modal from "../../../component/ui/modal";
import { get_countries, get_states } from "../../../services/apis/locations";
import {
  create_company,
  update_company,
} from "../../../services/apis/companies";
import { clearStates } from "../../../store/slices/locations";
import { useAppDispatch, useAppSelector } from "../../../store";
import { ICompany, IMutateCompany } from "../../../store/types/companies";

type ICreateCompany = {
  visible: boolean;
  onHandler: () => void;
  company?: ICompany | null;
};

type TextField =
  | "name"
  | "phone"
  | "email"
  | "address"
  | "website"
  | "rc_number";

const emptyCompany: IMutateCompany = {
  name: "",
  phone: "",
  email: "",
  address: "",
  website: "",
  rc_number: "",
  country_id: 0,
  state_id: 0,
};

const CreateCompany: React.FC<ICreateCompany> = ({
  visible,
  onHandler,
  company,
}) => {
  const dispatch = useAppDispatch();
  const countries = useAppSelector(({ locations }) => locations.countries);
  const states = useAppSelector(({ locations }) => locations.states);
  const loading = useAppSelector(({ companies }) => companies.loading);
  const [data, setData] = React.useState<IMutateCompany>(emptyCompany);

  React.useEffect(() => {
    if (!visible) return;

    get_countries(dispatch);
    setData(
      company
        ? {
            name: company.name,
            phone: company.phone,
            email: company.email,
            address: company.address,
            website: company.website,
            rc_number: company.rc_number,
            country_id: company.country_id,
            state_id: company.state_id,
          }
        : emptyCompany,
    );
    dispatch(clearStates());
    if (company) get_states(dispatch, company.country_id);
  }, [company, dispatch, visible]);

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setData((previous) => ({ ...previous, [name as TextField]: value }));
  };

  const onSubmit = async () => {
    if (
      !data.name.trim() ||
      !data.phone.trim() ||
      !data.email.trim() ||
      !data.address.trim() ||
      !data.country_id ||
      !data.state_id
    ) {
      toast.error("Complete the required company details and location");
      return;
    }

    const succeeded = company
      ? await update_company(company.id, data, dispatch)
      : await create_company(data, dispatch);
    if (succeeded) onHandler();
  };

  return (
    <Modal
      title={company ? "Edit Company" : "Register Company"}
      open={visible}
      onClose={onHandler}
      onOkClick={onSubmit}
      variant="large"
      isLoading={loading}
    >
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput
          label="Company Name"
          name="name"
          onChange={onChangeText}
          placeholder="Company Name"
          type="text"
          value={data.name}
          required
        />
        <TextInput
          label="Company Email"
          name="email"
          onChange={onChangeText}
          placeholder="Company Email"
          type="email"
          value={data.email}
          required
        />
        <TextInput
          label="Company Phone"
          name="phone"
          onChange={onChangeText}
          placeholder="Company Phone"
          type="text"
          value={data.phone}
          required
        />
        <TextInput
          label="RC Number"
          name="rc_number"
          onChange={onChangeText}
          placeholder="RC Number"
          type="text"
          value={data.rc_number}
        />
        <TextInput
          label="Company Address"
          name="address"
          onChange={onChangeText}
          placeholder="Company Address"
          type="text"
          value={data.address}
          required
        />
        <TextInput
          label="Company Website"
          name="website"
          onChange={onChangeText}
          placeholder="Company Website"
          type="text"
          value={data.website}
        />
        <SelectOption
          items={countries}
          label="Country"
          onChange={(value) => {
            const countryId = Number(value);
            setData((previous) => ({
              ...previous,
              country_id: countryId,
              state_id: 0,
            }));
            dispatch(clearStates());
            if (countryId) get_states(dispatch, countryId);
          }}
          placeholder="Select Country"
          value={data.country_id ? String(data.country_id) : ""}
        />
        <SelectOption
          items={states}
          label="State"
          onChange={(value) =>
            setData((previous) => ({
              ...previous,
              state_id: Number(value),
            }))
          }
          placeholder="Select State"
          value={data.state_id ? String(data.state_id) : ""}
        />
      </div>
    </Modal>
  );
};

export default CreateCompany;
